<template>
  <div>
    <fin-data-loader :resolver="resolver">
      <template #content>
        <v-container class="mx-0"
                     fluid
                     v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
                     v-style:paddingRight="$valueByBreakpoint($finPadding.right)">
          <v-row>
            <v-col cols="12" md="4" order="2" order-md="1">
              <fin-portfolio-value-change :addSign="false"
                                          :color="$finColors.primaryDark"
                                          :from="portfolioInitialValue"
                                          :to="portfolioCurrentValue"
                                          :value-date="portfolioCurrentValueDate"
                                          class="fin-portfolio-value-change"
                                          hint="Wertentwicklung seit Kauf"
                                          title="Aktueller Wert">
              </fin-portfolio-value-change>
            </v-col>
            <v-col cols="12" md="4" order="3" order-md="2">
              <fin-portfolio-value :valueLine="portfolioYield"
                                   class="fin-portfolio-rendite"
                                   title="Rendite seit Start">
                <template #valueSubline>
                  <v-row class="mt-4">
                    <v-col>
                      <div class="portfolio-value-box__hint">Heute</div>
                      <div :style="{color: portfolio1DColor}">
                        {{ portfolioYield1D | percent }}
                      </div>
                    </v-col>
                    <v-col v-if="portfolioYield1Y">
                      <div class="portfolio-value-box__hint">Seit&nbsp;einem&nbsp;Jahr</div>
                      <div :style="{color: portfolio1YColor}">
                        {{ portfolioYield1Y | percent }}
                      </div>
                    </v-col>
                  </v-row>
                </template>
              </fin-portfolio-value>
            </v-col>
            <v-col cols="12" md="4" order="1" order-md="3">
              <div class="d-flex justify-end">
                <v-btn outlined @click="downloadInstrumentData" class="mt-2" v-if="downloadInstrumentAllowed">Download Instrument Data</v-btn>
                <v-btn outlined @click="openLog" class="ml-4 mt-2">Portfolio-Log</v-btn>
                <v-menu :nudge-bottom=10
                        :nudge-left=50
                        :offset-y="true"
                        bottom>
                  <template #activator="{ on }">
                    <v-btn class="ml-4 mt-2 fin-strategy-musterportfolio-contextmenu" icon outlined tile v-on="on"
                           @click.native="contextMenuClicked">
                      <v-icon>icon-contextmenu</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="exportExcel" v-if="!isIos && !isMobile">
                      <v-list-item-title>
                        Als Excel speichern
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="showTransactions">
                      <v-list-item-title>
                        Historie
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <br/>
              <div :class="`d-flex ${$vuetify.breakpoint.mdAndUp ? 'justify-end' : ''}`">
                <fin-lexicon-text :style="$vuetify.breakpoint.mdAndUp ? 'max-width: 400px' : ''"
                                  auto-solve
                                  text="Hier findest du ein paar Erläuterungen darüber, was sich hinter dem Musterportfolio einer Strategie verbirgt."/>
              </div>
            </v-col>
          </v-row>
        </v-container>
        <v-container fluid
                     v-style:paddingLeft="$valueByBreakpoint($finPadding.left)"
                     v-style:paddingRight="$valueByBreakpoint($finPadding.right)"
                     :style="{  backgroundColor: $finColors.primaryLight }"
                     class="mt-6 pt-6">
          <v-row>
            <v-col>

              <fin-portfolio-instruments :instruments="instruments"
                                         :portfolio="portfolio"
                                         :strategy="strategy"
                                         class="box-instruments-table fin-portfolio-instruments"
                                         sticky-last-col
                                         transaction-dialog-title="Transaktionen"
                                         :editMode="false"/>
            </v-col>
          </v-row>
        </v-container>
        <v-container>
          <v-row>
            <v-col cols="12" md="5"
                   class="mt-6">
              <h4 style="font-size: 24px">Branchenverteilung</h4>
              <fin-chart2 :options="distributionChartOptions"
                          class="fin-portfolio-pie-chart"
                          constructor-type="chart"/>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </fin-data-loader>

    <fin-transactions-list-dialog v-model="showTransactionsDialog"
                                  v-if="showTransactionsDialog"
                                  only-history
                                  :instruments="instruments"
                                  :selected-view-id=selectedViewId
                                  :portfolio-id="portfolio.id"
                                  :full-screen="$vuetify.breakpoint.xs"
                                  global-transactions/>

    <fin-dialog v-if="showLog"
                v-model="showLog"
                :fullscreen="$vuetify.breakpoint.xs"
                :max-width="$vuetify.breakpoint.mdAndDown ? 750 : $vuetify.breakpoint.xl ? 1600 : 1000"
                persistent
                @input="(val) => {if (!val){closeLogDialog}}">
      <template #title>
        <h1>Portfolio-Log</h1>
      </template>

      <fin-data-loader :resolver="loadLogEntry">
        <template #content>
          <fin-time-line v-if="logEntry && logEntry.data && logEntry.data.group && logEntry.data.group.length > 0"
                         :entries="logEntry.data.group"/>
          <p v-else>Die Informationen konnten nicht geladen werden. Versuche es bitte später erneut.</p>
        </template>
      </fin-data-loader>

      <template #actions>
        <v-btn dark
               @click="showLog = false">
          Schließen
        </v-btn>
      </template>
    </fin-dialog>
  </div>
</template>

<script>
import FinDataLoader from "@/components/common/DataLoader"
import FinPortfolioInstruments from "@/components/pages/myPortfolio/PortfolioInstruments"
import FinPortfolioValueChange from "@/components/pages/myPortfolio/PortfolioValueChange"
import FinPortfolioValue from "@/components/pages/myPortfolio/PortfolioValue"
import FinChart2 from "@/components/common/Chart2/FinChart2"
import AmplitudePush from "@/mixins/AmplitudePush"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import {exportPortfolio, getStrategyShareDistribution} from "@/js/finMutations"
import FinTransactionsListDialog from "@/components/common/transactions/ListDialog"
import finBrowser from "@/js/finBrowser"
import FinTimeLine from "@/components/common/Timeline"
import {
  getStrategySamplePortfolio,
  getStrategySamplePortfolioTimeSeriesRTLastValues
} from "@/components/pages/finStrategy/details/tabs/templateportfolio/graphql"
import FinLexiconText from "@/components/common/lexicon/LexiconText"
import {PRICEMODEL_FEATURE_IDS} from "@/js/finPriceModel"
import PriceModel from "@/mixins/PriceModel"

export default {
  name: "FinShareTemplatePortfolio",
  components: {
    FinLexiconText,
    FinTimeLine,
    FinTransactionsListDialog,
    FinChart2,
    FinPortfolioValue,
    FinPortfolioValueChange,
    FinPortfolioInstruments,
    FinDataLoader
  },
  mixins: [AmplitudePush, PriceModel],
  props: {
    strategy: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      portfolio: {},
      instruments: [],
      assetClass: 'TOTAL',
      portfolioRatios: undefined,
      showLog: false,
      logEntry: undefined,
      selectedViewId: 'DEFAULT',
      showTransactionsDialog: false,
      isIos: finBrowser.IS_IOS(),
      isMobile: finBrowser.IS_MOBILE(),
    }
  },
  computed: {
    portfolioInitialValue() {
      let result = this.portfolioRatios
      if (!result || !result.currentValue || !result.growthAmount) {
        return null
      }
      return result.currentValue - result.growthAmount
    },
    downloadInstrumentAllowed() {
      return this.priceModelEnabledAndLevels(PRICEMODEL_FEATURE_IDS.STRATEGIES.id)
        .find(l => l.id === "STRATEGY_REBALANCING_DETAILS")
    },
    portfolioCurrentValue() {
      return this.portfolioRatios?.currentValue || 0
    },
    portfolioCurrentValueDate() {
      return this.portfolioRatios?.timestamp
    },
    portfolioYield() {
      return this.portfolioRatios?.yield || 0
    },
    portfolioYield1D() {
      return this.portfolioRatios?.yield1D || 0
    },
    portfolioYield1Y() {
      return this.portfolioRatios?.yield1Y || 0
    },
    portfolio1DColor() {
      let result = this.$finColors.accentGreen
      if (this.portfolioRatios?.yield1D < 0) {
        result = this.$finColors.accentRed
      }
      return result
    },
    portfolio1YColor() {
      let result = this.$finColors.accentGreen
      if (this.portfolioRatios?.yield1Y < 0) {
        result = this.$finColors.accentRed
      }
      return result
    },
    portfolioDistribution() {
      return this.portfolio?.distributions || []
    },
    distributionChartOptions() {
      const data = this.portfolioDistribution.find(item => item.assetClass === "SHARE")?.distribution.map(item => {
        return {
          name: `${item.name} ${item.y}%`,
          y: item.y
        }
      })
      return {
        chart: {
          type: 'pie'
        },
        credits: {
          enabled: false,
        },
        series: [
          {
            data
          },
        ],
        tooltip: {
          formatter: function () {
            return this.key
          }
        }
      }
    }
  },
  methods: {
    async resolver() {
      const result = await API.graphql(graphqlOperation(getStrategySamplePortfolio, {
        strategyId: this.strategy.id
      }))
      if (result) {
        this.portfolio = result.data.getStrategySamplePortfolio

        if (this.portfolio) {
          this.instruments = this.portfolio.shareEntries.items

          const ratios = await API.graphql(graphqlOperation(getStrategySamplePortfolioTimeSeriesRTLastValues, {
            portfolioId: this.portfolio.id,
            assetClass: 'TOTAL'
          }))
          this.portfolioRatios = ratios?.data?.getPortfolioRatioDataRealTime
        }
      }
    },
    async downloadInstrumentData() {
      console.log("calling downloadInstrumentData, this.strategy.id", this.strategy.id)
      try {
        const result = await API.graphql(graphqlOperation(getStrategyShareDistribution, {
          strategy: this.strategy.id
        }))
        const signedURL = result.data.getStrategyShareDistribution
        if (signedURL) {
          const a = document.createElement("a")
          a.href = signedURL
          a.setAttribute('download', this.strategy.name + ".xlsx")
          a.target = '_blank'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }
      } catch (e) {
        console.error("failed to download Strategy composition ", this.strategy.id, e)
        this.$toasted.global.finToast({
          message: 'Das Strategiezusammenstellung für die Strategie %name% konnte nicht heruntergeladen werden.',
          placeholders: [{name: "name", content: this.strategy.name}],
          type: 'error'
        })
      }
    },
    openLog() {
      this.trackToAmplitude('button', 'open_portfolio_log')
      this.showLog = true
    },
    async loadLogEntry() {
      const entryId = (this.strategy.id + '_Log').toLowerCase()
      const queryDocId = this.$prismic.Predicates.at('my.musterportfoliolog.uid', entryId)
      const query = [queryDocId]
      let queryResults = await this.$prismic.client.query(query, {
        // TODO accessToken
        pageSize: 1
      })
      this.logEntry = queryResults.results[0]
      // sort entries by date desc
      this.logEntry.data.group = this.logEntry.data.group.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    },
    closeLogDialog() {
      this.showLog = false
    },
    contextMenuClicked() {
      this.trackToAmplitude('contextmenu', 'open_context_menu')
    },
    async exportExcel() {
      console.log("calling exportPortfolio, this.portfolio.id", this.portfolio.id)
      let exportURL = undefined
      try {
        exportURL = await API.graphql(graphqlOperation(exportPortfolio, {
          portfolioId: this.portfolio.id,
          format: 'XLSX',
        }))
        this.trackToAmplitude('contextmenu', 'export_portfolio')

        if (exportURL.data.exportPortfolio) {
          const fileName = exportURL.data.exportPortfolio.substr(exportURL.data.exportPortfolio.indexOf('/Download') + 1)
          const signedURL = await this.$Amplify.Storage.get(fileName, {level: 'private'})

          if (signedURL) {
            const a = document.createElement("a")
            a.href = signedURL
            a.setAttribute('download', exportURL.data.exportPortfolio.substr(exportURL.data.exportPortfolio.lastIndexOf('/') + 1))
            a.target = '_blank'

            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }
        }
      } catch (e) {
        console.error("failed to download Musterportfolio ", this.strategy.id, this.portfolio.id, e)
        this.$toasted.global.finToast({
          message: 'Das Musterportfolio für die Strategie %name% konnte nicht heruntergeladen werden.',
          placeholders: [{name: "name", content: this.strategy.name}],
          type: 'error'
        })
      }
    },
    showTransactions() {
      this.trackToAmplitude('contextmenu', 'show_transactions')
      this.showTransactionsDialog = true
    },
    trackToAmplitude(positionOnPage, action) {
      const tab = 'tab_musterportfolio_'
      const event = {
        'event': 'fincraft_strategie_details_' + tab + positionOnPage + "_" + action,
        'step': action,
        'placement': tab + positionOnPage,
        'vue_breakpoint': this.$vuetify.breakpoint.name
      }
      this.pushToAmplitude(event)
    },
  }
}
</script>

<style lang="scss" scoped>

</style>
